import { useRef, useCallback, useEffect } from "react";
import { useMapInitialization } from "./useMapInitialization";
import { usePointInteractions } from "./usePointInteractions";
import { usePolygonManagement } from "./usePolygonManagement";
import { useMapStyleManager } from "./useMapStyleManager";
import { useMapNavigation } from "./useMapNavigation";
import { useHexagonManagement } from "./useHexagonManagement";
import {
  addHexagonsToMap,
  addHexagonInteractivity,
} from "../utils/hexagonUtils";

export const useMapController = ({
  mapContainer,
  points,
  zoom,
  mapboxStyles,
  selectedEstablishmentId,
  handleMarkerClick,
  polygons,
  showHexagons,
  hexagonConfig,
  memoizedHexagons,
}) => {
  const pulsingDotRef = useRef(null);

  // Hook de inicialización del mapa
  const { mapInstance, isMapReady, initialZoomRef, initialCenterRef } =
    useMapInitialization({
      mapContainer,
      points,
      zoom,
      mapboxStyles,
      selectedEstablishmentId,
      onMapReady: (map) => {
      },
    });


  const shouldExecuteHooks = mapInstance && isMapReady;

  const {
    lastClickedId,
    initializePoints,
    updatePointsData,
    cleanupLayers: cleanupPointLayers,
    resetSelection: resetPointSelection,
  } = usePointInteractions({
    mapInstance: shouldExecuteHooks ? mapInstance : null,
    points,
    handleMarkerClick,
    initialZoomRef,
    initialCenterRef,
  });

  // Hook de gestión de polígonos 
  const { addPolygonsToMap, cleanupPolygonLayers, addPolygonsImproved } =
    usePolygonManagement({
      mapInstance: shouldExecuteHooks ? mapInstance : null,
      polygons,
    });

  // Hook de gestión de hexágonos 
  const { cleanupHexagonLayers, reAddHexagons, toggleHexagonVisibility } =
    useHexagonManagement({
      mapInstance: shouldExecuteHooks ? mapInstance : null,
      showHexagons,
      hexagonConfig,
      points,
      memoizedHexagons,
      handleMarkerClick,
    });

  // Hook de gestión de estilos 
  const { cleanupMapElements, restoreMapLayers } = useMapStyleManager({
    mapInstance: shouldExecuteHooks ? mapInstance : null,
    mapboxStyles,
    points,
    polygons,
    showHexagons,
    memoizedHexagons,
    handleMarkerClick,
    updatePulsingDot: updatePointsData,
    addPolygonsToMap,
    addHexagonsToMap,
    addHexagonInteractivity,
    reInitializePoints: initializePoints, 
  });

  // Hook de navegación 
  const { navigateToPoint, returnToInitialView, fitToAllPoints } =
    useMapNavigation({
      mapInstance: shouldExecuteHooks ? mapInstance : null,
      selectedEstablishmentId,
      points,
    });

  // Effect para inicializar todos los elementos 
  useEffect(() => {
    if (!shouldExecuteHooks) return;

    if (points.length > 0) {
      initializePoints(mapInstance);

      setTimeout(() => {
        updatePointsData(mapInstance);
      }, 300);
    }

    if (showHexagons && points.length > 0) {
      setTimeout(() => {
        addHexagonsToMap(mapInstance, memoizedHexagons, (map) =>
          addHexagonInteractivity(map, handleMarkerClick)
        );
      }, 500);
    }

    if (polygons && polygons.length > 0) {
      setTimeout(() => {
        addPolygonsImproved(mapInstance);
      }, 300);
    }
  }, [shouldExecuteHooks, points, showHexagons, polygons, memoizedHexagons]);

  // Effect para resetear selección cuando cambien los puntos (filtros)
  useEffect(() => {
    if (shouldExecuteHooks && resetPointSelection) {
      resetPointSelection();
    }
  }, [points, shouldExecuteHooks, resetPointSelection]);

  // Función de cleanup general
  const performCompleteCleanup = useCallback(() => {
    if (mapInstance) {
      try {
        cleanupPointLayers(mapInstance);
        cleanupPolygonLayers(mapInstance);
        cleanupHexagonLayers(mapInstance);

        pulsingDotRef.current = null;
      } catch (error) {
      }
    }
  }, [
    mapInstance,
    cleanupPointLayers,
    cleanupPolygonLayers,
    cleanupHexagonLayers,
  ]);

  return {
    // Referencias y estado
    mapInstance,
    initialZoomRef,
    initialCenterRef,
    lastClickedId,
    pulsingDotRef,

    // Funciones de gestión de puntos
    initializePoints,
    updatePointsData,
    cleanupPointLayers,
    resetPointSelection,

    // Funciones de gestión de polígonos
    addPolygonsToMap,
    addPolygonsImproved,
    cleanupPolygonLayers,

    // Funciones de gestión de hexágonos
    reAddHexagons,
    toggleHexagonVisibility,
    cleanupHexagonLayers,

    // Funciones de navegación
    navigateToPoint,
    returnToInitialView,
    fitToAllPoints,

    // Funciones de gestión de estilos
    cleanupMapElements,
    restoreMapLayers,

    // Funciones de cleanup
    performCompleteCleanup,
  };
};
